import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminAPI } from '../../shared/api/admin-api';
import { useNotification } from '../../shared/contexts/NotificationContext';
import IconSelector from '../components/IconSelector';

interface CategoryFormData {
  name: string;
  description: string;
  active: boolean;
  imageType: 'image' | 'icon' | 'none';
  image: File | null;
  icon: string;
}

function CategoryForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success, error } = useNotification();
  
  const categoryId = searchParams.get('edit');
  const isNew = searchParams.get('new') === 'true';
  const isEdit = !!categoryId;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    active: true,
    imageType: 'none',
    image: null,
    icon: '',
  });
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    if (isEdit && categoryId) {
      loadCategory(parseInt(categoryId));
    }
  }, [isEdit, categoryId]);

  const loadCategory = async (id: number) => {
    try {
      const category = await AdminAPI.get(`categories/${id}`) as any;
      
      // Determinar tipo de imagem/ícone
      let imageType: 'image' | 'icon' | 'none' = 'none';
      let imagePreviewUrl = '';
      let iconName = '';
      
      if (category.image) {
        // Verificar se é um ícone (começa com "fa-") ou imagem
        if (typeof category.image === 'string' && category.image.startsWith('fa-')) {
          imageType = 'icon';
          iconName = category.image;
        } else {
          imageType = 'image';
          imagePreviewUrl = category.image;
        }
      }
      
      setFormData({
        name: category.name || '',
        description: category.description || '',
        active: category.active !== false,
        imageType,
        image: null,
        icon: iconName,
      });
      
      setImagePreview(imagePreviewUrl);
    } catch (err) {
      console.error('Erro ao carregar categoria:', err);
      error('Erro ao carregar categoria');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageTypeChange = (type: 'image' | 'icon' | 'none') => {
    setFormData((prev) => ({ ...prev, imageType: type }));
    if (type !== 'image') {
      setImagePreview('');
      setFormData((prev) => ({ ...prev, image: null }));
    }
    if (type !== 'icon') {
      setFormData((prev) => ({ ...prev, icon: '' }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
      e.target.value = '';
    }
  };

  const handleIconSelect = (icon: string) => {
    setFormData((prev) => ({ ...prev, icon }));
  };

  const removeImage = () => {
    if (imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setFormData((prev) => ({ ...prev, image: null }));
    setImagePreview('');
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imageData = null;
      
      if (formData.imageType === 'image' && formData.image) {
        // Converter imagem para base64
        imageData = await fileToBase64(formData.image);
      } else if (formData.imageType === 'image' && imagePreview && !imagePreview.startsWith('blob:')) {
        // Manter imagem existente
        imageData = imagePreview;
      } else if (formData.imageType === 'icon' && formData.icon) {
        // Usar ícone (salvar como string "fa-...")
        imageData = formData.icon;
      }

      const categoryData = {
        name: formData.name,
        description: formData.description,
        active: formData.active,
        image: imageData,
      };

      let result;
      if (isEdit && categoryId) {
        result = await AdminAPI.put(`categories/${categoryId}`, categoryData);
        success('Categoria atualizada com sucesso!');
      } else {
        result = await AdminAPI.post('categories', categoryData);
        success('Categoria criada com sucesso!');
      }

      setTimeout(() => {
        navigate('/admin/categorias');
      }, 1000);
    } catch (err: any) {
      console.error('Erro ao salvar categoria:', err);
      error(err.message || 'Erro ao salvar categoria');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-content">
      <form className="product-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="name">
            Nome da Categoria *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="form-input"
            value={formData.name}
            onChange={handleInputChange}
            required
            placeholder="Ex: Camisetas"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="description">
            Descrição
          </label>
          <textarea
            id="description"
            name="description"
            className="form-textarea"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Descreva a categoria..."
          />
        </div>

        {/* Seleção de Imagem ou Ícone */}
        <div className="form-group">
          <label className="form-label">
            Imagem ou Ícone (Opcional)
          </label>
          
          <div className="image-icon-toggle">
            <button
              type="button"
              className={`toggle-option ${formData.imageType === 'none' ? 'active' : ''}`}
              onClick={() => handleImageTypeChange('none')}
            >
              <i className="fas fa-ban"></i>
              <span>Nenhum</span>
            </button>
            <button
              type="button"
              className={`toggle-option ${formData.imageType === 'image' ? 'active' : ''}`}
              onClick={() => handleImageTypeChange('image')}
            >
              <i className="fas fa-image"></i>
              <span>Imagem</span>
            </button>
            <button
              type="button"
              className={`toggle-option ${formData.imageType === 'icon' ? 'active' : ''}`}
              onClick={() => handleImageTypeChange('icon')}
            >
              <i className="fas fa-icons"></i>
              <span>Ícone</span>
            </button>
          </div>

          {/* Upload de Imagem */}
          {formData.imageType === 'image' && (
            <>
              <div className="image-upload">
                <input
                  type="file"
                  id="category-image"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <label htmlFor="category-image" style={{ cursor: 'pointer' }}>
                  <i className="fas fa-camera" style={{ fontSize: '32px', color: 'var(--text-secondary)', marginBottom: '8px' }}></i>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Clique para adicionar imagem
                  </div>
                </label>
              </div>

              {imagePreview && (
                <div className="image-preview" style={{ marginTop: '12px' }}>
                  <div className="image-preview-item">
                    <img src={imagePreview} alt="Preview" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={removeImage}
                      aria-label="Remover imagem"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Seletor de Ícone */}
          {formData.imageType === 'icon' && (
            <IconSelector
              selectedIcon={formData.icon}
              onIconSelect={handleIconSelect}
            />
          )}
        </div>

        <div className="form-group">
          <label className="form-checkbox">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleInputChange}
            />
            <span>Categoria ativa</span>
          </label>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/categorias')}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Salvando...' : isEdit ? 'Atualizar' : 'Criar'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CategoryForm;

